import React, { useEffect, useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Autocomplete, Box, Button, Checkbox, Chip, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton, MenuItem, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useAuth } from '../../providers/AuthProvider';
import {
  attendanceService, CourseSession, CourseSessionInput, SESSION_TYPES, SessionImportResult,
} from '../../services/attendanceService';
import { tdGroupService, TDGroup } from '../../services/tdGroupService';
import { userAdminService, AdminUser } from '../../services/userAdminService';
import { formatSessionDate, formatSessionTime, teacherNames } from './attendanceUi';

const EMPTY_FORM: CourseSessionInput = {
  session_type: 'TD', date: '', start_time: '', end_time: '', td_group: null, label: '', room: '', teacher_ids: [],
};

const IMPORT_STATUS_COLOR: Record<string, 'success' | 'default' | 'warning' | 'error'> = {
  to_create: 'success', created: 'success', to_update: 'default', updated: 'default',
  skipped: 'default', duplicate: 'warning', invalid_group: 'error',
};

// Paramétrage des séances (admin) : import du fichier de service Excel, ou édition une par une.
// Les dates TP/Projet saisies ici alimentent aussi la page Charts (ex-GROUP_SESSIONS en dur).
const SessionsAdminTab: React.FC = () => {
  const { getValidToken } = useAuth();
  const [sessions, setSessions] = useState<CourseSession[]>([]);
  const [tdGroups, setTdGroups] = useState<TDGroup[]>([]);
  const [teachers, setTeachers] = useState<AdminUser[]>([]);
  const [groupFilter, setGroupFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  // Import Excel
  const [file, setFile] = useState<File | null>(null);
  const [importResult, setImportResult] = useState<SessionImportResult | null>(null);
  const [groupMap, setGroupMap] = useState<Record<string, string>>({});
  const [deleteMissing, setDeleteMissing] = useState(false);
  const [importLoading, setImportLoading] = useState(false);

  // Édition
  const [editing, setEditing] = useState<CourseSession | 'new' | null>(null);
  const [form, setForm] = useState<CourseSessionInput>(EMPTY_FORM);
  const [confirmDelete, setConfirmDelete] = useState<CourseSession | null>(null);

  const withToken = async () => {
    const token = await getValidToken();
    if (!token) throw new Error('Session expirée, reconnectez-vous.');
    return token;
  };

  const loadSessions = async () => {
    setLoading(true);
    try {
      setSessions(await attendanceService.listSessions(await withToken()));
    } catch (e: any) {
      setError(e.message || 'Erreur lors du chargement des séances');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSessions();
    (async () => {
      try {
        const token = await withToken();
        setTdGroups(await tdGroupService.list(token));
        const users = await userAdminService.listUsers(token);
        setTeachers(users.filter(u => u.access === 'teacher' || u.access === 'admin')
          .sort((a, b) => (a.lastname || '').localeCompare(b.lastname || '')));
      } catch (e: any) {
        setError(e.message || 'Erreur lors du chargement des groupes et enseignant·es');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runImport = async (apply: boolean) => {
    if (!file) return;
    setImportLoading(true);
    setError(null);
    setMessage(null);
    try {
      const result = await attendanceService.importSessions(file, { apply, groupMap, deleteMissing }, await withToken());
      setImportResult(result);
      if (apply) {
        setMessage(result.message + (result.deleted ? `, ${result.deleted} séance(s) supprimée(s)` : ''));
        await loadSessions();
      }
    } catch (e: any) {
      setError(e.message || "Erreur lors de l'import");
    } finally {
      setImportLoading(false);
    }
  };

  const openEdit = (session: CourseSession | 'new') => {
    setEditing(session);
    setForm(session === 'new' ? EMPTY_FORM : {
      session_type: session.session_type, date: session.date, start_time: session.start_time || '',
      end_time: session.end_time || '', td_group: session.td_group, label: session.label || '', room: session.room || '',
      teacher_ids: session.teachers.map(t => t.id),
    });
  };

  const handleSaveSession = async () => {
    if (!editing) return;
    setError(null);
    try {
      const token = await withToken();
      const payload = { ...form, start_time: form.start_time || null, end_time: form.end_time || null };
      const result = editing === 'new'
        ? await attendanceService.createSession(payload, token)
        : await attendanceService.updateSession(editing.id, payload, token);
      setMessage(result.message);
      setEditing(null);
      await loadSessions();
    } catch (e: any) {
      setError(e.message || "Erreur lors de l'enregistrement de la séance");
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      const result = await attendanceService.deleteSession(confirmDelete.id, await withToken());
      setMessage(result.message);
      setConfirmDelete(null);
      await loadSessions();
    } catch (e: any) {
      setError(e.message || 'Erreur lors de la suppression');
    }
  };

  const filtered = groupFilter === ''
    ? sessions
    : sessions.filter(s => (groupFilter === 'promo' ? s.td_group === null : s.td_group === groupFilter));
  const importCounts = importResult?.rows.reduce<Record<string, number>>((acc, r) => ({ ...acc, [r.status]: (acc[r.status] || 0) + 1 }), {});

  return (
    <>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message}</Alert>}

      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">Importer le fichier de service (xlsx)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Colonnes attendues : Type, Date, Heure début, Heure fin, Salle, Étudiants, Enseignants, REMARQUE.
            Une séance déjà en base (même date, heure de début, groupe et type) est mise à jour : les présences saisies sont conservées.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
            <Button variant="outlined" component="label" startIcon={<UploadFileIcon />}>
              Choisir un fichier
              <input type="file" hidden accept=".xlsx" onChange={(e) => { setFile(e.target.files?.[0] || null); setImportResult(null); setGroupMap({}); }} />
            </Button>
            {file && <Typography variant="body2">{file.name}</Typography>}
            <FormControlLabel control={<Checkbox checked={deleteMissing} onChange={(e) => setDeleteMissing(e.target.checked)} />}
              label="Supprimer les séances absentes du fichier (sauf si des présences y sont saisies)" />
            <Button variant="outlined" disabled={!file || importLoading} onClick={() => runImport(false)}>Prévisualiser</Button>
            <Button variant="contained" disabled={!file || importLoading || !importResult} onClick={() => runImport(true)}>
              {importLoading ? <CircularProgress size={20} /> : "Confirmer l'import"}
            </Button>
          </Box>

          {importResult && importResult.unresolved_groups.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Groupes du fichier non reconnus : associez-les à un groupe TD puis prévisualisez à nouveau (sinon leurs lignes sont ignorées).
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {importResult.unresolved_groups.map(token => (
                  <TextField key={token} select size="small" label={token} value={groupMap[token] || ''} sx={{ minWidth: 140 }}
                    onChange={(e) => setGroupMap({ ...groupMap, [token]: e.target.value })}>
                    <MenuItem value="">—</MenuItem>
                    {tdGroups.map(g => <MenuItem key={g.code} value={g.code}>{g.code}</MenuItem>)}
                  </TextField>
                ))}
              </Box>
            </Alert>
          )}
          {importResult && importResult.unknown_teachers.length > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Sans compte enseignant (gardés comme simple intitulé, ne pourront pas saisir l'appel) : {importResult.unknown_teachers.join(', ')}
            </Alert>
          )}
          {importResult && importResult.missing_sessions.length > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {importResult.missing_sessions.length} séance(s) en base absente(s) du fichier
              {deleteMissing ? ' : supprimées à la confirmation, sauf celles qui ont des présences saisies.' : ' : conservées.'}
            </Alert>
          )}
          {importCounts && (
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              {Object.entries(importCounts).map(([status, count]) => (
                <Chip key={status} label={`${status} : ${count}`} color={IMPORT_STATUS_COLOR[status] || 'default'} />
              ))}
            </Box>
          )}
          {importResult && (
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Ligne</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Séance</TableCell>
                    <TableCell>Étudiants</TableCell>
                    <TableCell>Groupe TD</TableCell>
                    <TableCell>Encadrant·es reconnu·es</TableCell>
                    <TableCell>Statut</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {importResult.rows.map(row => (
                    <TableRow key={row.line}>
                      <TableCell>{row.line}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.date ? formatSessionDate(row.date) : ''}</TableCell>
                      <TableCell>{row.type}{row.label && row.label !== row.type ? ` · ${row.label}` : ''}</TableCell>
                      <TableCell>{row.audience}</TableCell>
                      <TableCell>{row.date ? (row.td_group || (row.status === 'invalid_group' ? '?' : 'Promo')) : ''}</TableCell>
                      <TableCell>
                        {(row.teachers || []).join(', ')}
                        {row.warnings.map(w => <Typography key={w} variant="caption" color="warning.main" display="block">{w}</Typography>)}
                      </TableCell>
                      <TableCell>{row.message}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </AccordionDetails>
      </Accordion>

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
        <TextField select size="small" label="Groupe" value={groupFilter} sx={{ minWidth: 160 }} onChange={(e) => setGroupFilter(e.target.value)}>
          <MenuItem value="">Toutes les séances</MenuItem>
          <MenuItem value="promo">Promo entière</MenuItem>
          {tdGroups.map(g => <MenuItem key={g.code} value={g.code}>{g.code}</MenuItem>)}
        </TextField>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => openEdit('new')}>Nouvelle séance</Button>
        <Typography variant="body2" color="text.secondary">{filtered.length} séance(s)</Typography>
        {loading && <CircularProgress size={24} />}
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Horaire</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Intitulé</TableCell>
              <TableCell>Groupe</TableCell>
              <TableCell>Salle</TableCell>
              <TableCell>Encadrant·es</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map(s => (
              <TableRow key={s.id}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatSessionDate(s.date)}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatSessionTime(s)}</TableCell>
                <TableCell>{s.session_type}</TableCell>
                <TableCell>{s.label}</TableCell>
                <TableCell>{s.td_group || 'Promo'}</TableCell>
                <TableCell>{s.room}</TableCell>
                <TableCell>{teacherNames(s)}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  <Tooltip title="Modifier"><IconButton size="small" onClick={() => openEdit(s)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Supprimer"><IconButton size="small" color="error" onClick={() => setConfirmDelete(s)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={editing !== null} onClose={() => setEditing(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing === 'new' ? 'Nouvelle séance' : 'Modifier la séance'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField select label="Type" size="small" value={form.session_type} sx={{ minWidth: 120 }}
              onChange={(e) => setForm({ ...form, session_type: e.target.value })}>
              {SESSION_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
            <TextField label="Intitulé" size="small" fullWidth value={form.label || ''} placeholder="TD3, QUIZ 1 – TP1, PRJ5…"
              onChange={(e) => setForm({ ...form, label: e.target.value })} />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField label="Date" type="date" size="small" value={form.date} slotProps={{ inputLabel: { shrink: true } }}
              onChange={(e) => setForm({ ...form, date: e.target.value })} />
            <TextField label="Début" type="time" size="small" value={form.start_time || ''} slotProps={{ inputLabel: { shrink: true } }}
              onChange={(e) => setForm({ ...form, start_time: e.target.value })} />
            <TextField label="Fin" type="time" size="small" value={form.end_time || ''} slotProps={{ inputLabel: { shrink: true } }}
              onChange={(e) => setForm({ ...form, end_time: e.target.value })} />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField select label="Groupe" size="small" value={form.td_group || ''} sx={{ minWidth: 160 }}
              onChange={(e) => setForm({ ...form, td_group: e.target.value || null })}>
              <MenuItem value="">Promo entière</MenuItem>
              {tdGroups.map(g => <MenuItem key={g.code} value={g.code}>{g.code}</MenuItem>)}
            </TextField>
            <TextField label="Salle" size="small" fullWidth value={form.room || ''} onChange={(e) => setForm({ ...form, room: e.target.value })} />
          </Box>
          <Autocomplete
            multiple
            size="small"
            options={teachers}
            value={teachers.filter(t => form.teacher_ids.includes(t.id))}
            onChange={(_, value) => setForm({ ...form, teacher_ids: value.map(t => t.id) })}
            getOptionLabel={(t) => `${t.lastname || ''} ${t.firstname || ''} (${t.login})`}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(params) => <TextField {...params} label="Chargé·es de TD/TP" />}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Annuler</Button>
          <Button variant="contained" disabled={!form.date || !form.session_type} onClick={handleSaveSession}>Enregistrer</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmDelete !== null} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Supprimer la séance ?</DialogTitle>
        <DialogContent>
          {confirmDelete && `${formatSessionDate(confirmDelete.date)} ${confirmDelete.start_time || ''} — ${confirmDelete.session_type} ${confirmDelete.label || ''} (${confirmDelete.td_group || 'Promo'})`}
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>Les présences déjà saisies pour cette séance seront supprimées.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Annuler</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Supprimer</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SessionsAdminTab;
